import type { JSX } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  BarChart3,
  CalendarClock,
  CreditCard,
  Film,
  HelpCircle,
  History,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  PenLine,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Store,
  Users,
  Video,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/shared/lib/cn";

interface MerchantNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface MerchantNavGroup {
  label: string;
  items: MerchantNavItem[];
  dock?: "bottom";
}

const MERCHANT_NAV_GROUPS: MerchantNavGroup[] = [
  {
    label: "总览",
    items: [{ to: "/dashboard", label: "工作台", icon: LayoutDashboard }],
  },
  {
    label: "创作",
    items: [
      { to: "/create/single", label: "单次创作", icon: PenLine },
      { to: "/create/workflow", label: "智能工作流", icon: Sparkles },
      { to: "/create/drafts", label: "草稿箱", icon: ListChecks },
      { to: "/create/tasks", label: "任务中心", icon: Film },
    ],
  },
  {
    label: "资产",
    items: [
      { to: "/assets/products", label: "商品管理", icon: Store },
      { to: "/assets/viral", label: "爆款素材库", icon: ImageIcon },
      { to: "/assets/avatars", label: "数字分身", icon: Users },
      { to: "/assets/history", label: "生成历史", icon: History },
    ],
  },
  {
    label: "发布",
    items: [
      { to: "/publish/calendar", label: "发布日历", icon: CalendarClock },
      { to: "/publish/accounts", label: "平台账号", icon: Send },
      { to: "/publish/review", label: "待审核", icon: ShieldCheck },
      { to: "/publish/history", label: "发布历史", icon: History },
    ],
  },
  {
    label: "分析",
    items: [
      { to: "/analytics/videos", label: "视频效果", icon: BarChart3 },
      { to: "/analytics/products", label: "商品效果", icon: BarChart3 },
    ],
  },
  {
    label: "设置",
    items: [
      { to: "/settings/team", label: "团队成员", icon: Users },
      { to: "/settings/roles", label: "角色权限", icon: ShieldCheck },
      { to: "/settings/account", label: "账户设置", icon: Settings },
      { to: "/settings/quota", label: "配额与用量", icon: CreditCard },
      { to: "/settings/extension", label: "浏览器插件", icon: KeyRound },
    ],
  },
  {
    label: "支持",
    dock: "bottom",
    items: [{ to: "/help", label: "帮助中心", icon: HelpCircle }],
  },
];

export interface SidebarProps {
  appName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ appName, isOpen, onClose }: SidebarProps): JSX.Element {
  const topGroups = MERCHANT_NAV_GROUPS.filter((group) => group.dock !== "bottom");
  const bottomGroups = MERCHANT_NAV_GROUPS.filter((group) => group.dock === "bottom");

  return (
    <>
      <aside
        aria-label="商家端主导航"
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-gray-100 bg-white p-10 transition-transform duration-500 ease-in-out lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <Link
          to="/dashboard"
          onClick={onClose}
          aria-label={`${appName} 首页`}
          className="mb-12 flex items-center gap-3"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black shadow-lg shadow-black/20">
            <Video className="text-white" size={28} />
          </div>

          <div className="min-w-0">
            <p className="text-2xl font-black tracking-tighter text-black">AI VIDEO</p>
            <p className="truncate text-[11px] font-black uppercase tracking-[0.32em] text-gray-300">
              {appName}
            </p>
          </div>
        </Link>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="space-y-8 pr-2">
            {topGroups.map((group) => (
              <SidebarGroup
                key={group.label}
                label={group.label}
                items={group.items}
                onNavigate={onClose}
              />
            ))}
          </div>

          <div className="mt-8 border-t border-gray-100 pt-8 pr-2">
            {bottomGroups.map((group) => (
              <SidebarGroup
                key={group.label}
                label={group.label}
                items={group.items}
                onNavigate={onClose}
              />
            ))}
          </div>
        </div>
      </aside>

      {isOpen ? (
        <button
          type="button"
          aria-label="关闭导航"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-md lg:hidden"
        />
      ) : null}
    </>
  );
}

interface SidebarGroupProps {
  label: string;
  items: MerchantNavItem[];
  onNavigate: () => void;
}

function SidebarGroup({ label, items, onNavigate }: SidebarGroupProps): JSX.Element {
  return (
    <section>
      <p className="mb-3 px-6 text-[11px] font-black uppercase tracking-[0.32em] text-gray-300">
        {label}
      </p>

      <div className="space-y-2">
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-4 rounded-2xl px-6 py-4 font-bold transition-all duration-300",
                  isActive
                    ? "bg-black text-white shadow-2xl shadow-black/20"
                    : "text-gray-400 hover:bg-gray-50 hover:text-black",
                )
              }
            >
              <Icon size={22} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </section>
  );
}
